import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { MapPin, Clock, Camera, ArrowLeft, Check, AlertTriangle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ReportSightingPage() {
  const { petId } = useParams();
  const navigate = useNavigate();
  const [pet, setPet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [locationLoading, setLocationLoading] = useState(false);

  const [formData, setFormData] = useState({
    location: '',
    latitude: null,
    longitude: null,
    time_seen: '',
    message: ''
  });

  useEffect(() => {
    const fetchPet = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/pets/${petId}`);
        setPet(response.data);
      } catch (err) {
        toast.error('Pet not found');
        navigate('/');
      } finally {
        setLoading(false);
      }
    };
    fetchPet();
  }, [petId, navigate]);

  const handleGetLocation = async () => {
    setLocationLoading(true);
    try {
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 10000
        });
      });
      
      const { latitude, longitude } = position.coords;
      setFormData({
        ...formData,
        latitude,
        longitude,
        location: `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`
      });
      toast.success('Location captured!');
    } catch (err) {
      toast.error('Could not get your location. Please enter manually.');
    } finally {
      setLocationLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post(`${BACKEND_URL}/api/sightings`, {
        pet_id: petId,
        ...formData
      });
      setSubmitted(true);
      toast.success('Sighting reported successfully!');
    } catch (error) {
      toast.error('Failed to report sighting. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-24 pb-20">
          <div className="max-w-lg mx-auto px-4 text-center">
            <div className="bg-card rounded-2xl p-12 border border-border shadow-sm">
              <div className="w-20 h-20 bg-primary/15 rounded-full flex items-center justify-center mx-auto mb-6">
                <Check className="w-10 h-10 text-primary" />
              </div>
              <h1 className="text-2xl font-heading font-bold text-foreground">
                Thank You!
              </h1>
              <p className="mt-4 text-muted-foreground">
                Your sighting report has been sent to {pet.pet_name}&apos;s owner. 
                They will be notified immediately.
              </p>
              <div className="mt-8 space-y-4">
                <Link to={`/pet/${petId}`}>
                  <Button 
                    className="w-full bg-primary hover:bg-primary/90 rounded-full py-6"
                    data-testid="back-to-profile-btn"
                  >
                    Back to {pet.pet_name}&apos;s Profile
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="pt-24 pb-20" data-testid="report-sighting-page">
        <div className="max-w-lg mx-auto px-4 sm:px-6">
          {/* Back Link */}
          <Link 
            to={`/pet/${petId}`}
            className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to {pet?.pet_name}&apos;s profile
          </Link>

          <div className="bg-card rounded-2xl border border-border shadow-sm p-8">
            {/* Alert Banner */}
            {pet?.is_lost && (
              <div className="bg-destructive/15 border border-destructive/30 rounded-lg p-4 mb-6 flex items-center gap-3">
                <AlertTriangle className="w-5 h-5 text-destructive flex-shrink-0" />
                <p className="text-sm text-destructive-foreground">
                  <strong>{pet.pet_name}</strong> is currently missing. Your sighting report will help the owner find them!
                </p>
              </div>
            )}

            <h1 className="text-2xl font-heading font-bold text-foreground mb-2">
              Report a Sighting
            </h1>
            <p className="text-muted-foreground mb-8">
              If you&apos;ve seen {pet?.pet_name} but couldn&apos;t catch them, let the owner know where you spotted them.
            </p>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Location */}
              <div>
                <Label htmlFor="location">Location</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    id="location"
                    value={formData.location}
                    onChange={(e) => setFormData({...formData, location: e.target.value})}
                    placeholder="Enter location or use GPS"
                    className="flex-1"
                    data-testid="sighting-location-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleGetLocation}
                    disabled={locationLoading}
                    data-testid="get-location-btn"
                  >
                    <MapPin className="w-4 h-4" />
                  </Button>
                </div>
                {formData.latitude && (
                  <p className="text-xs text-primary mt-1">
                    GPS coordinates captured
                  </p>
                )}
              </div>

              {/* Time Seen */}
              <div>
                <Label htmlFor="time_seen">When did you see them?</Label>
                <div className="relative mt-1">
                  <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    id="time_seen"
                    type="datetime-local"
                    value={formData.time_seen}
                    onChange={(e) => setFormData({...formData, time_seen: e.target.value})}
                    className="pl-10"
                    data-testid="sighting-time-input"
                  />
                </div>
              </div>

              {/* Message */}
              <div>
                <Label htmlFor="message">Additional Details (Optional)</Label>
                <Textarea
                  id="message"
                  value={formData.message}
                  onChange={(e) => setFormData({...formData, message: e.target.value})}
                  placeholder="Any helpful details - direction they were heading, what they were doing, etc."
                  className="mt-1"
                  rows={4}
                  data-testid="sighting-message-input"
                />
              </div>

              <Button 
                type="submit"
                className="w-full bg-primary hover:bg-primary/90 rounded-full py-6"
                disabled={submitting}
                data-testid="submit-sighting-btn"
              >
                {submitting ? 'Submitting...' : 'Submit Sighting Report'}
              </Button>
            </form>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
